import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { NAV_RESET_EVENT_GROUPS } from './Header';
import {
  CALIFORNIA_LESSON_TIMES,
  DALLAS_LESSON_TIMES,
  MASON_LESSON_TIMES,
  REGISTRATION_EVENTS,
  UPCOMING_EVENT_GROUPS
} from '../config/event-registration-config';
import { FormattedEventDescription } from '../utils/format-event-description';
import { resolveEventLogoSrc } from '../config/event-logos';
import { partitionRegistrationKeys } from '../utils/partition-registration-keys';
import './UpcomingEventGroups.css';

const GROUP_LESSON_TIMES = {
  'california-2026': CALIFORNIA_LESSON_TIMES,
  'dallas-2026': DALLAS_LESSON_TIMES,
  'mason-2026': MASON_LESSON_TIMES
};

/**
 * @param {'home' | 'events'} variant
 * @param {Array} [pastEvents]
 * @param {(event: object) => void} [onPastEventClick]
 * @param {Array} [groups]
 * @param {(groupId: string | null) => void} [onSelectionChange]
 */
const UpcomingEventGroups = ({
  variant = 'home',
  groups = UPCOMING_EVENT_GROUPS,
  pastEvents = [],
  onPastEventClick,
  onSelectionChange
}) => {
  const location = useLocation();
  const [selectedGroupId, setSelectedGroupId] = useState(null);
  const [showPastEvents, setShowPastEvents] = useState(false);

  useEffect(() => {
    if (location.state?.[NAV_RESET_EVENT_GROUPS] == null) return;
    setSelectedGroupId(null);
    setShowPastEvents(false);
  }, [location.state]);

  useEffect(() => {
    onSelectionChange?.(selectedGroupId);
  }, [selectedGroupId, onSelectionChange]);

  useEffect(() => {
    if (!selectedGroupId) {
      setShowPastEvents(false);
    }
  }, [selectedGroupId]);

  const isHome = variant === 'home';
  const cardClass = isHome ? 'home-event-card' : 'card upcoming-group-card';
  const headerClass = isHome ? 'home-event-header' : 'upcoming-group-header';
  const actionBtnClass = isHome ? 'home-learn-more-btn' : 'learn-more-btn';
  const renderGroupLogos = (group) => {
    if (!group.logos?.length) return null;

    return (
      <div className="upcoming-event-logos">
        {group.logos.map((logo) => (
          <img
            key={logo.src}
            src={resolveEventLogoSrc(logo.src)}
            alt={logo.alt}
            className={
              logo.src === 'dallas-logo'
                ? 'upcoming-dallas-logo'
                : logo.src === '/logo.png'
                  ? 'upcoming-splash-logo'
                  : undefined
            }
          />
        ))}
      </div>
    );
  };

  const renderGroupTitleRow = (group) => (
    <div className="upcoming-group-title-row">
      <h3>{group.title}</h3>
      {renderGroupLogos(group)}
    </div>
  );

  const renderGroupSummary = (registrationKeys, groupId) => {
    const first = REGISTRATION_EVENTS[registrationKeys[0]];
    if (!first) return null;

    const omitLocationPrefix =
      groupId === 'dallas-2026' ||
      groupId === 'mason-2026' ||
      groupId === 'california-2026';
    const locationPrefix =
      !omitLocationPrefix && first.location ? `${first.location} ` : '';

    return (
      <>
        {locationPrefix}
        <FormattedEventDescription text={first.description} />
      </>
    );
  };

  const renderGroupTextBlock = (group) => (
    <>
      {renderGroupTitleRow(group)}
      {group.subtitle ? (
        <p className="upcoming-group-subtitle">{group.subtitle}</p>
      ) : null}
      <p className="upcoming-group-description">
        {renderGroupSummary(group.registrationKeys, group.id)}
      </p>
    </>
  );

  const renderDateRegisterButtons = (
    keys,
    { pastDates = false, lessonTimes = null } = {}
  ) => (
    <div className="upcoming-date-register-list">
      {keys.map((key) => {
        const event = REGISTRATION_EVENTS[key];
        if (!event) return null;
        const dateLabel = event.weekday
          ? `${event.date} (${event.weekday})`
          : event.date;
        const isDisabled = Boolean(event.registrationDisabled);
        const className = [
          'upcoming-date-register-btn',
          pastDates ? 'upcoming-date-register-btn--past' : null,
          isDisabled ? 'upcoming-date-register-btn--disabled' : null
        ]
          .filter(Boolean)
          .join(' ');

        const content = (
          <>
            <span className="upcoming-date-register-main">
              <span className="upcoming-date-register-label">
                {isDisabled
                  ? 'Registration closed'
                  : pastDates
                    ? 'Past event'
                    : 'Register for'}
              </span>
              <span className="upcoming-date-register-date">{dateLabel}</span>
              {lessonTimes ? (
                <span className="upcoming-date-register-time">
                  <strong>Time:</strong> {lessonTimes}
                </span>
              ) : null}
            </span>
            <span className="upcoming-date-register-action">
              {isDisabled ? 'Unavailable' : pastDates ? 'View' : 'Register'}
            </span>
          </>
        );

        if (isDisabled) {
          return (
            <span
              key={key}
              className={className}
              aria-disabled="true"
              title="Registration is temporarily unavailable"
            >
              {content}
            </span>
          );
        }

        return (
          <Link key={key} to={`/register/${key}`} className={className}>
            {content}
          </Link>
        );
      })}
    </div>
  );

  const renderPastEventsList = () => {
    if (!pastEvents.length) return null;

    const pastCardClass = isHome ? 'home-event-card' : 'card upcoming-past-event-card';
    const pastHeaderClass = isHome ? 'home-event-header' : 'upcoming-group-header';
    const pastBtnClass = 'past-event-learn-more-btn';

    return (
      <div className="upcoming-past-events-block">
        <h4 className="upcoming-past-events-heading">Past Events</h4>
        {pastEvents.map((event) => (
          <div key={event.id} className={pastCardClass}>
            <div className={pastHeaderClass}>
              <h3>{event.title}</h3>
              <button
                type="button"
                className={pastBtnClass}
                onClick={() => onPastEventClick?.(event)}
              >
                Learn More
              </button>
            </div>
          </div>
        ))}
      </div>
    );
  };

  const selectedGroup = groups.find((g) => g.id === selectedGroupId);

  if (selectedGroup) {
    const { upcoming, past } = partitionRegistrationKeys(selectedGroup.registrationKeys);
    const hasPastRegistrationDates = past.length > 0;
    const hasHistoricalPastEvents = pastEvents.length > 0;
    const canShowPastSection = hasPastRegistrationDates || hasHistoricalPastEvents;

    return (
      <div className="upcoming-event-groups upcoming-event-groups--detail">
        <div className={cardClass}>{renderGroupTextBlock(selectedGroup)}</div>

        {upcoming.length > 0 ? (
          <>
            <p className="upcoming-select-date">Select a date to register:</p>
            {renderDateRegisterButtons(upcoming, {
              lessonTimes: GROUP_LESSON_TIMES[selectedGroup.id] || null
            })}
          </>
        ) : null}

        {showPastEvents && hasPastRegistrationDates ? (
          <>
            <p className="upcoming-select-date upcoming-select-date--past">
              Past registration dates:
            </p>
            {renderDateRegisterButtons(past, {
              pastDates: true,
              lessonTimes: GROUP_LESSON_TIMES[selectedGroup.id] || null
            })}
          </>
        ) : null}

        {canShowPastSection ? (
          <div className="upcoming-past-toggle-wrap">
            {!showPastEvents ? (
              <button
                type="button"
                className="upcoming-see-past-btn"
                onClick={() => setShowPastEvents(true)}
              >
                See past events
              </button>
            ) : (
              <>
                {renderPastEventsList()}
                <button
                  type="button"
                  className="upcoming-hide-past-btn"
                  onClick={() => setShowPastEvents(false)}
                >
                  Hide past events
                </button>
              </>
            )}
          </div>
        ) : null}

        <button
          type="button"
          className={`${actionBtnClass} upcoming-back-btn`}
          onClick={() => setSelectedGroupId(null)}
        >
          Back to Upcoming Events
        </button>
      </div>
    );
  }

  return (
    <div className="upcoming-event-groups">
      {groups.map((group) => (
        <div key={group.id} className={cardClass}>
          <div className={headerClass}>
            <div className="upcoming-group-list-text">
              {renderGroupTitleRow(group)}
              {group.subtitle ? (
                <p className="upcoming-group-subtitle">{group.subtitle}</p>
              ) : null}
            </div>
            <button
              type="button"
              className={actionBtnClass}
              onClick={() => setSelectedGroupId(group.id)}
            >
              Registration
            </button>
          </div>
          <p className="upcoming-group-description">
            {renderGroupSummary(group.registrationKeys, group.id)}
          </p>
        </div>
      ))}
    </div>
  );
};

export default UpcomingEventGroups;
